import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import supabase from '../../lib/supabaseClient';

const Configurador = () => {
  const [loading, setLoading] = useState(false);
  const [funis, setFunis] = useState([]);
  const [funilSelecionadoId, setFunilSelecionadoId] = useState('');
  const [etapas, setEtapas] = useState([]);

  const [novoFunilNome, setNovoFunilNome] = useState('');
  const [novaEtapaNome, setNovaEtapaNome] = useState('');
  const [novaEtapaSigla, setNovaEtapaSigla] = useState('');

  const funilSelecionado = useMemo(
    () => funis.find((f) => f.id === funilSelecionadoId) || null,
    [funis, funilSelecionadoId]
  );

  const carregarFunis = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('funis')
        .select('*')
        .order('criado_em', { ascending: true });

      if (error) throw error;
      setFunis(data || []);

      if (!funilSelecionadoId && data && data.length > 0) {
        setFunilSelecionadoId(data[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar funis:', err);
    } finally {
      setLoading(false);
    }
  };

  const carregarEtapas = async (funilId) => {
    if (!funilId) {
      setEtapas([]);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('funis_etapas')
        .select('*')
        .eq('funil_id', funilId)
        .order('ordem', { ascending: true });

      if (error) throw error;
      setEtapas(data || []);
    } catch (err) {
      console.error('Erro ao carregar etapas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarFunis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (funilSelecionadoId) {
      carregarEtapas(funilSelecionadoId);
    }
  }, [funilSelecionadoId]);

  const handleCriarFunil = async () => {
    if (!novoFunilNome.trim()) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('funis')
        .insert({ nome: novoFunilNome.trim() })
        .select()
        .single();

      if (error) throw error;

      setFunis((prev) => [...prev, data]);
      setNovoFunilNome('');
      setFunilSelecionadoId(data.id);
    } catch (err) {
      console.error('Erro ao criar funil:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarEtapa = async () => {
    if (!funilSelecionadoId || !novaEtapaNome.trim() || !novaEtapaSigla.trim()) return;

    try {
      setLoading(true);
      const proximaOrdem = (etapas[etapas.length - 1]?.ordem || 0) + 1;

      const { data, error } = await supabase
        .from('funis_etapas')
        .insert({
          funil_id: funilSelecionadoId,
          nome: novaEtapaNome.trim(),
          sigla: novaEtapaSigla.trim(),
          ordem: proximaOrdem,
        })
        .select()
        .single();

      if (error) throw error;

      setEtapas((prev) => [...prev, data].sort((a, b) => a.ordem - b.ordem));
      setNovaEtapaNome('');
      setNovaEtapaSigla('');
    } catch (err) {
      console.error('Erro ao adicionar etapa:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoverEtapa = async (etapaId) => {
    if (!window.confirm('Deseja realmente remover esta etapa?')) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('funis_etapas')
        .delete()
        .eq('id', etapaId);

      if (error) throw error;

      setEtapas((prev) => prev.filter((e) => e.id !== etapaId));
    } catch (err) {
      console.error('Erro ao remover etapa:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h5" component="h1">
          Configurador de Funis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Recarregar funis">
            <IconButton size="small" onClick={carregarFunis} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Lista de funis */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Funis de vendas
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Novo funil"
                fullWidth
                value={novoFunilNome}
                onChange={(e) => setNovoFunilNome(e.target.value)}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCriarFunil}
                disabled={loading || !novoFunilNome.trim()}
              >
                Criar
              </Button>
            </Box>

            <Divider sx={{ my: 1 }} />

            <List dense sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {funis.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nenhum funil cadastrado.
                </Typography>
              )}
              {funis.map((funil) => (
                <ListItemButton
                  key={funil.id}
                  selected={funil.id === funilSelecionadoId}
                  onClick={() => setFunilSelecionadoId(funil.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={funil.nome}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Etapas do funil selecionado */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, minHeight: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {funilSelecionado ? `Etapas do funil: ${funilSelecionado.nome}` : 'Selecione um funil'}
              </Typography>
            </Box>

            {funilSelecionado && (
              <>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Nome da etapa"
                    sx={{ minWidth: 200, flexGrow: 1 }}
                    value={novaEtapaNome}
                    onChange={(e) => setNovaEtapaNome(e.target.value)}
                  />
                  <TextField
                    size="small"
                    label="Sigla"
                    sx={{ width: 120 }}
                    value={novaEtapaSigla}
                    onChange={(e) => setNovaEtapaSigla(e.target.value.toUpperCase())}
                    inputProps={{ maxLength: 5 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAdicionarEtapa}
                    disabled={loading || !novaEtapaNome.trim() || !novaEtapaSigla.trim()}
                  >
                    Adicionar etapa
                  </Button>
                </Box>

                <Divider sx={{ my: 1 }} />

                {etapas.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma etapa cadastrada para este funil.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {etapas.map((etapa) => (
                      <Box
                        key={etapa.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          py: 0.75,
                          px: 1,
                          borderRadius: 1,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ minWidth: 24 }}>
                            {etapa.ordem}
                          </Typography>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {etapa.nome}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sigla: {etapa.sigla}
                            </Typography>
                          </Box>
                        </Box>
                        <Tooltip title="Remover etapa">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoverEtapa(etapa.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Configurador;
